import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Typography, IconButton, Pagination } from "@mui/material";
import CloseIcon from "../../assets/modal-closing-icon.png";
import downloadCloud from "../../assets/download-cloud-02.svg";
import {
  FiltersSection,
  FiltersRight,
  ExportButton,
  FilterExportText,
  StyledCard,
  FooterContainer,
  FooterText,
  orderPrice,
  renderCell,
  orderStyle,
  evenRowColor,
  oddRowColor,
  colStyle,
} from "./ShipmentSummary.styles";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { ShipmentType } from "../../types/Shipment";
import DataTable from "../../common/DataTable";
import PageHeader from "../../common/PageHeader";

//memoize clumns
const columns = [
  { label: "Customer", field: "customer" },
  { label: "Location", field: "location" },
  {
    label: "Order Details",
    field: "orderDetails",
    renderCell: (row: ShipmentType) => (
      <Box sx={renderCell}>
        <Typography variant="body2" sx={orderStyle}>
          {row.orderDetails.orderId}
        </Typography>
        <Typography variant="body2" sx={orderPrice}>
          {row.orderDetails.price}
        </Typography>
      </Box>
    ),
  },
  {
    label: "Truck No.",
    field: "truckNo",
  },
  {
    label: "Driver Name",
    field: "driverName",
  },
  {
    label: "Phone Number",
    field: "phone",
  },
];

const rowStyle = (index: number) => {
  return {
    backgroundColor: index % 2 === 0 ? evenRowColor : oddRowColor,
  };
};

const ShipmentSummary: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { state } = location;
  const { selectedOrders } = state || { selectedOrders: [] };
  const [pagination, setPagination] = useState({
    page: 1,
    rowsPerPage: 6,
    totalRecords: selectedOrders.length,
  });
  const orgId = localStorage.getItem("orgId");

  const handleDownload = async () => {
    const element = document.getElementById("shipment-summary");

    if (element) {
      const clonedElement = element.cloneNode(true) as HTMLElement;

      const actionButtons = clonedElement.querySelectorAll(".action-button");
      actionButtons.forEach((button) => {
        (button as HTMLElement).style.display = "none";
      });

      const paginationControls = clonedElement.querySelectorAll(
        ".MuiPagination-root"
      );
      paginationControls.forEach((control) => {
        (control as HTMLElement).style.display = "none";
      });

      const footer = clonedElement.querySelector(
        "footer, .MuiBox-root, .FooterContainer"
      );
      if (footer) {
        (footer as HTMLElement).style.display = "none";
      }

      const heading = document.createElement("h2");
      heading.innerText = "Shipment Summary";
      heading.style.textAlign = "center";
      heading.style.marginBottom = "12px";
      clonedElement.insertBefore(heading, clonedElement.firstChild);
      const tempContainer = document.createElement("div");
      tempContainer.style.position = "absolute";
      tempContainer.style.top = "-9999px";
      tempContainer.style.left = "-9999px";
      tempContainer.appendChild(clonedElement);
      document.body.appendChild(tempContainer);
      const canvas = await html2canvas(clonedElement, { scale: 2 });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      pdf.addImage(imgData, "PNG", 10, 10, 190, 0);
      pdf.save("ShipmentSummary.pdf");
      document.body.removeChild(tempContainer);
    }
  };

  const handleClose = () => {
    navigate(`/maltora-seller/shipment`);
  };

  const handleChangePage = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const { page, rowsPerPage } = pagination;
  const startIndex = (page - 1) * rowsPerPage;
  const currentRows = selectedOrders.slice(
    startIndex,
    startIndex + rowsPerPage
  );
  const endIndex = Math.min(
    startIndex + currentRows.length,
    pagination.totalRecords
  );
  const emptyRows = rowsPerPage - currentRows.length;
  return (
    <>
      <FiltersRight>
        <PageHeader title="Shipment Summary" />
        <IconButton color="secondary" onClick={handleClose}>
          <img
            src={CloseIcon}
            alt="Close"
            width={24}
            height={24}
            data-testid="close-button"
          />
        </IconButton>
      </FiltersRight>

      <StyledCard id="shipment-summary">
        <FiltersSection>
          <ExportButton onClick={handleDownload}>
            <img
              src={downloadCloud}
              alt="Download"
              width={19.23}
              height={19.23}
            />
            <FilterExportText>Export</FilterExportText>
          </ExportButton>
        </FiltersSection>
        <Box padding={0}>
          <DataTable
            columns={columns.map((col) => ({
              ...col,
              sx: { colStyle },
              cellStyle:
                col.field === "customer"
                  ? { fontWeight: 600, fontSize: "13px" }
                  : { fontSize: "11.63px" },
            }))}
            data={currentRows}
            rowStyle={rowStyle}
            emptyStateMessage="No shipments available"
            rowsPerPage={rowsPerPage}
            emptyRows={emptyRows}
          />
        </Box>

        <FooterContainer>
          <FooterText variant="subtitle2">
            Showing data {startIndex + 1} to {endIndex} of{" "}
            {pagination.totalRecords} entries
          </FooterText>
          <Pagination
            count={Math.ceil(pagination.totalRecords / pagination.rowsPerPage)}
            page={pagination.page}
            onChange={(_, newPage) => handleChangePage(newPage)}
            shape="rounded"
            variant="outlined"
          />
        </FooterContainer>
      </StyledCard>
    </>
  );
};

export default ShipmentSummary;
